import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";

import { Colors, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { createPortal } from "react-dom";

export interface MultiselectProps<T> {
  items: T[];
  selectedItems: T[];
  onItemSelect: (item: T) => void;
  onRemove: (item: T, index: number) => void;
  itemRenderer: (item: T) => ReactNode;
  tagRenderer: (item: T) => ReactNode;
  query: string;
  onQueryChange: (query: string) => void;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  fill?: boolean;
  onClearAll: () => void;
  inputStyle?: React.CSSProperties;
  onCreateArtificialObject?: (value: string) => T;
  onBatchItemSelect?: (items: T[]) => void;
}

export function Multiselect<T>({
  items,
  selectedItems,
  onItemSelect,
  onRemove,
  itemRenderer,
  tagRenderer,
  query,
  onQueryChange,
  disabled = false,
  placeholder = "Search...",
  autoFocus = false,
  onClearAll,
  inputStyle,
  onCreateArtificialObject,
  onBatchItemSelect,
}: MultiselectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  useEffect(() => {
    if (items.length > 0 && query) {
      setActiveIndex(0);
    }
  }, [items]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate and update dropdown position when it opens
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dialogContent = document.querySelector(
        '[data-slot="dialog-content"]',
      );

      if (dialogContent) {
        // If rendering inside dialog, calculate position relative to dialog
        const dialogRect = dialogContent.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom - dialogRect.top,
          left: rect.left - dialogRect.left,
          width: rect.width,
        });
      } else {
        // Fallback to normal positioning
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Check if the input contains commas (indicating multiple values)
    if (value.includes(",")) {
      // Split by comma and process each part
      const parts = value
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part);
      const lastPart = parts.pop() || ""; // Keep the last part as the current query

      // Process all complete parts (before the last comma)
      parts.forEach((part) => {
        if (part) {
          // Try to find existing item first
          const existingItem = items.find(
            (item) =>
              itemRenderer(item)?.toString().toLowerCase() ===
              part.toLowerCase(),
          );

          if (existingItem) {
            // Use existing item from search results
            onItemSelect(existingItem);
          } else if (onCreateArtificialObject) {
            // Create artificial object
            const artificialItem = onCreateArtificialObject(part);
            onItemSelect(artificialItem);
          }
        }
      });

      // Set the remaining query
      onQueryChange(lastPart);
      setIsOpen(lastPart.length > 0);
      setActiveIndex(-1);
    } else {
      // Normal single value input
      onQueryChange(value);
      setIsOpen(true);
      setActiveIndex(-1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedData = e.clipboardData.getData("text/plain");

    // Check if pasted data contains common separators
    if (
      pastedData.includes(",") ||
      pastedData.includes("\n") ||
      pastedData.includes(";") ||
      pastedData.includes(" ")
    ) {
      e.preventDefault();

      // Split by multiple separators, clean up the data
      const parts = pastedData
        .split(/[,\n; ]+/)
        .map((part) => part.trim())
        .filter((part) => part);

      // Collect all new items to add
      const newItems: T[] = [];

      // Process each part
      parts.forEach((part) => {
        if (part) {
          // Check if item already exists in current selections
          const alreadySelected = selectedItems.find(
            (item) =>
              itemRenderer(item)?.toString().toLowerCase() ===
              part.toLowerCase(),
          );

          if (!alreadySelected) {
            // Try to find existing item first
            const existingItem = items.find(
              (item) =>
                itemRenderer(item)?.toString().toLowerCase() ===
                part.toLowerCase(),
            );

            if (existingItem) {
              // Use existing item from search results
              newItems.push(existingItem);
            } else if (onCreateArtificialObject) {
              // Create artificial object
              const artificialItem = onCreateArtificialObject(part);
              newItems.push(artificialItem);
            }
          }
        }
      });

      // Add all new items at once to avoid race conditions
      if (newItems.length > 0) {
        // Call onChange through the onItemSelect logic, but batch the updates
        if (onBatchItemSelect) {
          onBatchItemSelect(newItems);
        } else {
          newItems.forEach((item) => onItemSelect(item));
        }
      }

      // Clear the input
      onQueryChange("");
      setIsOpen(false);
      setActiveIndex(-1);
    }
    // If no separators, let the default paste behavior handle it
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) {
        // Select from dropdown
        onItemSelect(items[activeIndex]);
        setIsOpen(false);
        setActiveIndex(-1);
      } else if (query.trim()) {
        // Create tag from current query if no dropdown item is selected
        const existingItem = items.find(
          (item) =>
            itemRenderer(item)?.toString().toLowerCase() ===
            query.toLowerCase(),
        );

        if (existingItem) {
          onItemSelect(existingItem);
        } else if (onCreateArtificialObject) {
          const artificialItem = onCreateArtificialObject(query.trim());
          onItemSelect(artificialItem);
        }
        setIsOpen(false);
        setActiveIndex(-1);
      }
    } else if (e.key === "," || e.key === "Tab") {
      // Handle comma or tab as tag creation
      e.preventDefault();
      if (query.trim()) {
        const existingItem = items.find(
          (item) =>
            itemRenderer(item)?.toString().toLowerCase() ===
            query.toLowerCase(),
        );

        if (existingItem) {
          onItemSelect(existingItem);
        } else if (onCreateArtificialObject) {
          const artificialItem = onCreateArtificialObject(query.trim());
          onItemSelect(artificialItem);
        }
        setIsOpen(false);
        setActiveIndex(-1);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
    } else if (
      e.key === "Backspace" &&
      query === "" &&
      selectedItems.length > 0
    ) {
      e.preventDefault();
      const lastIndex = selectedItems.length - 1;
      const lastItem = selectedItems[lastIndex];
      onRemove(lastItem, lastIndex);
    }
  };

  const handleItemClick = (item: T) => {
    onItemSelect(item);
    setIsOpen(false);
    setActiveIndex(-1);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Container ref={containerRef}>
      <InputContainer disabled={disabled}>
        <TagsContainer>
          {selectedItems.map((item, index) => (
            <Tag key={index}>
              {tagRenderer(item)}
              <RemoveButton
                onClick={() => !disabled && onRemove(item, index)}
                disabled={disabled}
              >
                <Icon icon="small-cross" color={Colors.GRAY1} />
              </RemoveButton>
            </Tag>
          ))}
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            placeholder={selectedItems.length === 0 ? placeholder : ""}
            disabled={disabled}
            autoFocus={autoFocus}
            style={inputStyle}
            onPaste={handlePaste}
          />
        </TagsContainer>
        {selectedItems.length > 0 && (
          <ClearButton
            onClick={() => !disabled && onClearAll?.()}
            disabled={disabled}
          >
            <Icon icon="cross" color={Colors.GRAY1} />
          </ClearButton>
        )}
      </InputContainer>
      {isOpen &&
        items.length > 0 &&
        createPortal(
          <DropdownPortal
            ref={dropdownRef}
            isInDialog={
              !!document.querySelector('[data-slot="dialog-content"]')
            }
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {items.map((item, index) => (
              <DropdownItem
                key={index}
                active={index === activeIndex}
                onClick={() => handleItemClick(item)}
              >
                {itemRenderer(item)}
              </DropdownItem>
            ))}
          </DropdownPortal>,
          document.querySelector('[data-slot="dialog-content"]') ||
            document.body,
        )}
    </Container>
  );
}

const Container = styled.div<{ fill?: boolean }>`
  position: relative;
  width: ${({ fill }) => (fill ? "100%" : "auto")};
`;

const InputContainer = styled.div<{ disabled?: boolean }>`
  border: 1px solid ${Colors.LIGHT_GRAY1};
  border-radius: 6px;
  background: white;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "text")};
  display: flex;
  align-items: flex-start;
  position: relative;
  padding-right: 32px;

  &:focus-within {
    border-color: ${Colors.BLUE5};
    box-shadow: 0 0 0 1px ${Colors.BLUE5};
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  min-height: 46px;
  padding: 4px 8px;
  flex: 1;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${Colors.LIGHT_GRAY4};
  padding: 18px 12px;
  font-size: 16px;
  gap: 4px;
  height: 24px;
  border-radius: 4px;
`;

const RemoveButton = styled.button<{ disabled?: boolean }>`
  background: none;
  border: none;
  color: ${Colors.GRAY1};
  font-size: 16px;
  padding: 0 2px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.5 : 0.8)};
  }
`;

const Input = styled.input`
  border: none;
  outline: none;
  padding: 4px;
  flex: 1;
  min-width: 60px;
  min-height: 24px;
  background: transparent;

  &:disabled {
    cursor: not-allowed;
  }
`;

const DropdownPortal = styled.div<{ isInDialog?: boolean }>`
  position: ${({ isInDialog }) => (isInDialog ? "absolute" : "fixed")};
  background: white;
  border-radius: 3px;
  box-shadow:
    0 0 0 1px rgba(16, 22, 26, 0.1),
    0 2px 4px rgba(16, 22, 26, 0.2);
  max-height: 320px;
  overflow-y: auto;
  z-index: 9999;
  padding: 4px;
  pointer-events: auto;
`;

const DropdownItem = styled.div<{ active: boolean }>`
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  background: ${({ active }) => (active ? Colors.BLUE3 : "transparent")};
  color: ${({ active }) => (active ? "white" : Colors.BLACK)};

  &:hover {
    background: ${({ active }) => (active ? Colors.BLUE3 : Colors.LIGHT_GRAY3)};
  }
`;

const ClearButton = styled.button<{ disabled?: boolean }>`
  background: none;
  border: none;
  color: ${Colors.GRAY1};
  padding: 4px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  position: absolute;
  top: 10px;
  right: 8px;
  min-width: 24px;
  min-height: 24px;

  &:hover {
    background: ${Colors.LIGHT_GRAY3};
    opacity: ${({ disabled }) => (disabled ? 0.5 : 0.8)};
  }
`;
